// =============================================================================
// projetsCommuns.js : aides des projets partagées entre le bundle principal
// (accueil Activité, Ma mutuelle, qui les reçoivent d'App.jsx) et la page
// « Projets, offres & solutions » (AthosProjectsView.js, paresseuse).
// Extrait d'App.jsx le 30/09/2026 (lot L11d.0), sans changement.
// =============================================================================
import React from "react";
import { encreSur } from "./navConstants";
import { RISQUES_ASSURANCE } from "./projetShared";
import { alpha } from "./alpha";


/**
 * Mappe l'ancien couple (type, statut) vers la nouvelle nomenclature
 * (categorie, sousType, phase|disponibilite|cadenceRevue).
 *
 * Utilisé :
 * 1. Au runtime : si projet.categorie est absent (legacy), on dérive à la volée
 *    pour les compteurs et l'affichage des nouvelles vues.
 * 2. Dans le script de migration : applique le mapping sur Firestore.
 *
 * Mapping documenté §1.4 du plan de refonte.
 */
function deriverCategorieFromLegacy(p) {
  if (!p) return { categorie: "projet" };
  const statut = p.statut || "";
  const type = p.type || "projet";
  // Cas Solution : statuts qui désignent un objet activable
  if (statut === "partenariat_ouvert" || type === "partenariat") {
    return {
      categorie: "solution",
      sousType: "partenariat",
      disponibilite: "ouverte",
    };
  }
  if (statut === "solution_ouverte") {
    return {
      categorie: "solution",
      sousType: p.offreProduit ? "offre" : "service",
      disponibilite: "ouverte",
    };
  }
  // Cas Mission : travail continu sans phase
  if (statut === "travail_continu") {
    return {
      categorie: "mission",
      sousType: "mission_permanente",
      cadenceRevue: "continue",
    };
  }
  // Sinon Projet : mapping des phases
  const phaseMap = {
    exploratoire:  "cadrage",
    intermediaire: "instruction",
    conclusion:    "instruction",
    attente:       "cadrage",
    realise:       "clos",
    abandonne:     "abandonne",
  };
  return {
    categorie: "projet",
    sousType: "construction",
    phase: phaseMap[statut] || "cadrage",
  };
}

/**
 * Lit la catégorie d'un projet : utilise projet.categorie si présent (post-
 * migration), sinon dérive à la volée depuis le legacy. Pratique pour les
 * compteurs et filtres avant que la migration Firestore soit lancée.
 */
function getCategorie(p) {
  return p?.categorie || deriverCategorieFromLegacy(p).categorie;
}

/**
 * Dérive le statut de maturité d'un projet.
 *
 * Priorités :
 *   1. Champ explicite `statutMaturite` (rempli par admin lors de la revue)
 *   2. Dérivation depuis categorie + phase + disponibilite
 *   3. Fallback "construction" pour les cas indéterminés
 *
 * Refonte 2026-05-20 : statut "acquis" supprimé. Rétrocompat : si un projet
 * a encore `statutMaturite: "acquis"` en base ou si la dérivation tomberait
 * dessus (phase "clos"/"abandonne"), on remappe vers "activable" pour les
 * solutions, "construction" pour les projets.
 */
function getStatutMaturite(p) {
  if (!p) return "construction";
  const cat = getCategorie(p);
  const raw = p.statutMaturite;
  if (raw && raw !== "acquis") return raw;
  if (raw === "acquis") {
    // Rétrocompat : un projet marqué "acquis" en base → on dérive
    return cat === "solution" ? "activable" : "construction";
  }
  // Dérivation (raw absent)
  if (cat === "solution") return "activable";
  if (cat === "mission") return "mission_permanente";
  // categorie = projet
  if (p.phase === "clos" || p.phase === "abandonne") return "construction";
  if (p.phase === "idee" || p.phase === "cadrage") return "etude";
  return "construction"; // instruction, deploiement
}

// Refonte 2026-05-20 : badge risque valorisé (couleur de fond pleine, icône,
// label en gras). Beaucoup plus visible que l'ancien texte gris discret.
// Si `risquesConcernesAutre` est passé et que "autre" est dans la liste,
// on affiche le texte libre au lieu de "Autre".
function RisqueBadges({ risques, autreLibre = "", compact = false, max = null }) {
  if (!Array.isArray(risques) || risques.length === 0) return null;
  const items = max ? risques.slice(0, max) : risques;
  const extra = max ? risques.length - max : 0;
  return (
    <span style={{display:"inline-flex",gap:4,flexShrink:0,
      flexWrap:compact?"nowrap":"wrap",alignItems:"center"}}>
      {items.map(rv => {
        const r = RISQUES_ASSURANCE.find(x => x.v === rv);
        if (!r) return null;
        const label = (rv === "autre" && autreLibre) ? autreLibre : r.l;
        return (
          <span key={rv} title={`Risque concerné : ${label}`}
            style={{
              display:"inline-flex",alignItems:"center",gap:4,
              fontSize: "var(--fs-micro)",
              fontWeight: 700,
              color: encreSur(r.color),
              background: r.color,
              padding: compact ? "2px 7px" : "3px 9px",
              borderRadius: "var(--radius-md)",
              lineHeight: 1.4,
              letterSpacing: "calc(.02em * var(--em-ajuste, 1))",
              whiteSpace: "nowrap",
              boxShadow: `0 1px 3px ${alpha(r.color, .333)}`,
            }}>
            <span>{label}</span>
          </span>
        );
      })}
      {extra > 0 && (
        <span style={{
          fontSize: "var(--fs-micro)", fontWeight: 700,
          color: "#6B7280", background: "#9CA3AF22",
          padding: "2px 7px", borderRadius: "var(--radius-md)",
        }} title={`+${extra} autre(s) risque(s)`}>+{extra}</span>
      )}
    </span>
  );
}

// Refonte 2026-05-20 : helper pour rendre les mots en gras dans un titre.
// Syntaxe markdown simple : **mot** → <strong>mot</strong>. Tout le reste
// est rendu en texte brut. Retourne un fragment React utilisable directement
// dans n'importe quel JSX. Utilisé pour les titres de projets dans les
// vues simplifiée / liste / mosaïque + ProjetFicheStructuree.
function renderBold(text) {
  if (!text || typeof text !== "string") return text || "";
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export { deriverCategorieFromLegacy, getCategorie, getStatutMaturite, RisqueBadges, renderBold };
